import { css } from 'aphrodite';
import { useId } from 'react';
import type { AssistantEmbedPosition } from '../../../../../utils/assistantUrlUtils';
import { MessageCircleIcon } from '../../../../../web/components/svgs/icons/MessageCircleIcon';
import {
	CustomerAssistantAvatar,
	CustomerAssistantContent,
	CustomerAssistantConversationStarterCard,
	CustomerAssistantConversationStarters,
	CustomerAssistantDescription,
	CustomerAssistantTitle,
} from '../../CustomerAssistantUI';
import { previewWidgetStyles } from './AssistantPreviewWidget.styles';
import { embeddedPreviewStyles as s } from './EmbeddedPreviewWidget.styles';

interface EmbeddedPreviewWidgetProps {
	name: string;
	description: string;
	avatarUrl: string | null;
	conversationStarters: string[];
	position: AssistantEmbedPosition;
	launcherLabel: string;
	primaryColor?: string;
	foregroundColor?: string;
}

function getContrastTextColor(hexBg: string): string {
	const shorthand = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/i.exec(hexBg);
	if (shorthand) {
		hexBg = '#' + shorthand[1] + shorthand[1] + shorthand[2] + shorthand[2] + shorthand[3] + shorthand[3];
	}
	const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexBg);
	if (!result) return '#ffffff';
	const channels = [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)].map(c => {
		c = c / 255;
		return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
	});
	const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
	return luminance > 0.179 ? '#000000' : '#ffffff';
}

export function EmbeddedPreviewWidget({
	name,
	description,
	avatarUrl,
	conversationStarters,
	position,
	launcherLabel,
	primaryColor,
	foregroundColor,
}: EmbeddedPreviewWidgetProps) {
	const id = useId();
	const nonEmptyStarters = conversationStarters.filter(starter => starter.trim());
	const trimmedLabel = launcherLabel.trim();
	const hasLabel = trimmedLabel.length > 0;
	const isLeft = position === 'bottom-left';
	const launcherStyle = primaryColor
		? { background: primaryColor, color: foregroundColor || getContrastTextColor(primaryColor) }
		: foregroundColor
			? { color: foregroundColor }
			: undefined;

	return (
		<div className={css(s.backdrop)}>
			<div className={css(s.backdropPattern)} aria-hidden='true' />

			{/* Widget root — anchored bottom-left or bottom-right */}
			<div className={css(s.widgetRoot, isLeft ? s.widgetRootLeft : s.widgetRootRight)}>
				{/* Launcher button */}
				<div className={css(s.launcher, hasLabel && s.launcherWithLabel)} style={launcherStyle} aria-hidden='true'>
					<MessageCircleIcon />
					{hasLabel ? <span className={css(s.launcherLabel)}>{trimmedLabel}</span> : null}
				</div>

				{/* Popup panel */}
				<div className={css(s.panel)}>
					<div className={css(s.panelClose)} aria-hidden='true'>
						&times;
					</div>
					<div className={css(s.panelContent)}>
						<CustomerAssistantContent styles={[previewWidgetStyles.container]}>
							{avatarUrl ? <CustomerAssistantAvatar imgSrc={avatarUrl} /> : null}
							<CustomerAssistantTitle styles={[previewWidgetStyles.name, !name && previewWidgetStyles.namePlaceholder]}>
								{name || 'Assistant Name'}
							</CustomerAssistantTitle>
							<CustomerAssistantDescription
								styles={[previewWidgetStyles.description, !description && previewWidgetStyles.descriptionPlaceholder]}
							>
								{description || 'Assistant description will appear here...'}
							</CustomerAssistantDescription>
							{nonEmptyStarters.length > 0 && (
								<CustomerAssistantConversationStarters styles={[previewWidgetStyles.conversationStartersContainer]}>
									{nonEmptyStarters.map((starter, index) => (
										<CustomerAssistantConversationStarterCard key={`${id}-starter-${index}`}>
											{starter}
										</CustomerAssistantConversationStarterCard>
									))}
								</CustomerAssistantConversationStarters>
							)}
						</CustomerAssistantContent>
					</div>
				</div>
			</div>
		</div>
	);
}
